import { useCallback, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { addCleaningRoom, selectCleaningRoomById, updateCleaningRoom } from "./cleaningRoomsSlice";
import { Box, Button, ButtonGroup, Container, FormControl, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack, TextField, Typography } from "@mui/material";
import { Localized } from "@fluent/react";
import { Link } from "react-router-dom";
import SaveIcon from '@mui/icons-material/Save';
import { CleanRoomClass } from "./types";
import { ISO_PARTICLE_LIMITS } from "./Utils/particleCalc";

export const CleaningRoomForm = () => {
    const { id: paramId } = useParams()
    const isNew = !paramId
    const entity = useAppSelector(state => paramId ? selectCleaningRoomById(state, paramId) : undefined)

    const [name, setName] = useState(entity?.name ?? '')
    const [number, setNumber] = useState(entity?.number ?? '')
    const [area, setArea] = useState(entity?.area !== null && entity?.area !== undefined ? String(entity.area) : '')
    const [volume, setVolume] = useState(entity?.volume !== null && entity?.volume !== undefined ? String(entity.volume) : '')
    const [address1, setAddress1] = useState(entity?.address1 ?? '')
    const [address2, setAddress2] = useState(entity?.address2 ?? '')
    const [city, setCity] = useState(entity?.city ?? '')
    const [state, setState] = useState(entity?.state ?? '')
    const [zipcode, setZipcode] = useState(entity?.zipcode ?? '')
    const [country, setCountry] = useState(entity?.country ?? '')
    const [image, setImage] = useState<string | null>(entity?.image ?? null)
    const [cleanlinessClass, setCleanlinessClass] = useState<CleanRoomClass | ''>(entity?.cleanlinessClass ?? '')
    const [maxParticles05um, setMaxParticles05um] = useState(
        entity?.maxParticles05um !== null && entity?.maxParticles05um !== undefined ? String(entity.maxParticles05um) : ''
    )
    const [maxParticles5um, setMaxParticles5um] = useState(
        entity?.maxParticles5um !== null && entity?.maxParticles5um !== undefined ? String(entity.maxParticles5um) : ''
    )
    const [nameError, setNameError] = useState(false)
    const [numberError, setNumberError] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const handleClassChange = useCallback((e: SelectChangeEvent<CleanRoomClass | ''>) => {
        const cls = e.target.value as CleanRoomClass | ''
        setCleanlinessClass(cls)
        if (cls && cls !== 'ISO 1-9') {
            const limits = ISO_PARTICLE_LIMITS[cls as CleanRoomClass]
            if (limits) {
                setMaxParticles05um(String(limits.max05um))
                setMaxParticles5um(String(limits.max5um))
            }
        }
    }, [])

    const handleImageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (evt) => {
            setImage(evt.target?.result as string)
        }
        reader.readAsDataURL(file)
    }, [])

    const handleSave = useCallback(() => {
        let valid = true
        if (!name.trim()) {
            setNameError(true)
            valid = false
        } else {
            setNameError(false)
        }
        if (!number.trim()) {
            setNumberError(true)
            valid = false
        } else {
            setNumberError(false)
        }
        if (!valid) return

        const parsedArea = area !== '' ? parseFloat(area) : null
        const parsedVolume = volume !== '' ? parseFloat(volume) : null
        const parsedClass = cleanlinessClass !== '' ? cleanlinessClass : null
        const parsedMax05um = maxParticles05um !== '' ? parseFloat(maxParticles05um) : null
        const parsedMax5um = maxParticles5um !== '' ? parseFloat(maxParticles5um) : null

        if (isNew) {
            const id = crypto.randomUUID()
            dispatch(addCleaningRoom({
                id,
                name,
                number,
                area: parsedArea,
                volume: parsedVolume,
                address1,
                address2,
                city,
                state,
                zipcode,
                country,
                image,
                cleanlinessClass: parsedClass,
                maxParticles05um: parsedMax05um,
                maxParticles5um: parsedMax5um,
            }))
            navigate(`/measurements/cleaning-rooms/edit/${id}`, { replace: true })
        } else if (entity) {
            dispatch(updateCleaningRoom({
                id: entity.id,
                changes: {
                    name,
                    number,
                    area: parsedArea,
                    volume: parsedVolume,
                    address1,
                    address2,
                    city,
                    state,
                    zipcode,
                    country,
                    image,
                    cleanlinessClass: parsedClass,
                    maxParticles05um: parsedMax05um,
                    maxParticles5um: parsedMax5um,
                },
            }))
            navigate('/measurements/cleaning-rooms')
        }
    }, [isNew, name, number, area, volume, address1, address2, city, state, zipcode, country, image, cleanlinessClass, maxParticles05um, maxParticles5um, entity, dispatch, navigate])

    return (
        <Container component={Paper} maxWidth={false} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Stack spacing={2} sx={{ maxWidth: 600 }}>
                <TextField
                    required
                    label={<Localized id="cleaning-room-name">Nazwa</Localized>}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    error={nameError}
                    inputProps={{ maxLength: 200 }}
                />
                <TextField
                    required
                    label={<Localized id="cleaning-room-number">Numer</Localized>}
                    value={number}
                    onChange={e => setNumber(e.target.value)}
                    error={numberError}
                    inputProps={{ maxLength: 50 }}
                />
                <TextField
                    label={<Localized id="cleaning-room-area">Powierzchnia (m²)</Localized>}
                    type="number"
                    value={area}
                    onChange={e => setArea(e.target.value)}
                />
                <TextField
                    label={<Localized id="cleaning-room-volume">Objętość (m³)</Localized>}
                    type="number"
                    value={volume}
                    onChange={e => setVolume(e.target.value)}
                />
                <FormControl>
                    <InputLabel id="cleanliness-class-label">
                        <Localized id="cleaning-room-cleanliness-class">Cleanliness class</Localized>
                    </InputLabel>
                    <Select
                        labelId="cleanliness-class-label"
                        label={<Localized id="cleaning-room-cleanliness-class">Cleanliness class</Localized>}
                        value={cleanlinessClass}
                        onChange={handleClassChange}
                    >
                        <MenuItem value=""><em>—</em></MenuItem>
                        <MenuItem value="A">GMP Class A</MenuItem>
                        <MenuItem value="B">GMP Class B</MenuItem>
                        <MenuItem value="C">GMP Class C</MenuItem>
                        <MenuItem value="D">GMP Class D</MenuItem>
                        <MenuItem value="ISO 1-9">ISO 1-9</MenuItem>
                    </Select>
                </FormControl>
                <Stack direction="row" spacing={2}>
                    <TextField
                        label={<Localized id="cleaning-room-max-particles-05um">Max ≥0.5 µm (p/m³)</Localized>}
                        type="number"
                        value={maxParticles05um}
                        onChange={e => setMaxParticles05um(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label={<Localized id="cleaning-room-max-particles-5um">Max ≥5.0 µm (p/m³)</Localized>}
                        type="number"
                        value={maxParticles5um}
                        onChange={e => setMaxParticles5um(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                </Stack>
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        <Localized id="cleaning-room-address">Adres</Localized>
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            label={<Localized id="address1">Adres 1</Localized>}
                            value={address1}
                            onChange={e => setAddress1(e.target.value)}
                            inputProps={{ maxLength: 200 }}
                        />
                        <TextField
                            label={<Localized id="address2">Adres 2</Localized>}
                            value={address2}
                            onChange={e => setAddress2(e.target.value)}
                            inputProps={{ maxLength: 200 }}
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label={<Localized id="city">Miasto</Localized>}
                                value={city}
                                onChange={e => setCity(e.target.value)}
                                inputProps={{ maxLength: 100 }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label={<Localized id="state">Województwo</Localized>}
                                value={state}
                                onChange={e => setState(e.target.value)}
                                inputProps={{ maxLength: 100 }}
                                sx={{ flex: 1 }}
                            />
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label={<Localized id="zipcode">Kod pocztowy</Localized>}
                                value={zipcode}
                                onChange={e => setZipcode(e.target.value)}
                                inputProps={{ maxLength: 20 }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label={<Localized id="country">Kraj</Localized>}
                                value={country}
                                onChange={e => setCountry(e.target.value)}
                                inputProps={{ maxLength: 100 }}
                                sx={{ flex: 1 }}
                            />
                        </Stack>
                    </Stack>
                </Box>
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        <Localized id="cleaning-room-image">Rysunek pomieszczenia</Localized>
                    </Typography>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleImageChange}
                    />
                    <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                        <Localized id="upload-image">Wgraj zdjęcie</Localized>
                    </Button>
                    {image && (
                        <Box sx={{ marginTop: 1 }}>
                            <img src={image} alt="room" style={{ maxWidth: '100%', maxHeight: 300 }} />
                        </Box>
                    )}
                </Box>
                <ButtonGroup color="secondary" variant="contained">
                    <Button startIcon={<SaveIcon />} onClick={handleSave}>
                        <Localized id="save">Zapisz</Localized>
                    </Button>
                    <Button component={Link} to="/measurements/cleaning-rooms" color="secondary" variant="outlined">
                        <Localized id="back">Wróć</Localized>
                    </Button>
                </ButtonGroup>
            </Stack>
        </Container>
    )
}
